import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"

// Timesheets → Codes & lists → Mapping and Export (Kloepfer K6-8, Heather
// 2026-09-29; built 2026-10-09). The office says which Interac job each
// project posts to (and the company's own work: the yard, the shop), which
// phase each phone cost code posts to, and each person's labor type, whose
// phase is used when the cost code has none. The Export section shows the
// week's Interac time file - its counts and the lines still missing a code -
// and downloads it. Data comes from /timesheets/mapping and every change
// answers with the mapping again. The pane itself is shown and hidden by
// time_lists_controller.js, which owns the pop-up; both sit on the page's
// root element. Each list has a word search on top (K8-22): every word typed
// has to be somewhere in the row, accents and capitals ignored.
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c])

const SEL = "py-1 pl-2 pr-7 border border-gray-300 rounded-lg text-[13px] bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
const INPUT = "px-2 py-1 border border-gray-300 rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
const TH = "px-2 py-1.5 text-left text-[13px] font-medium text-gray-500 bg-gray-50 whitespace-nowrap"
const TD = "px-2 py-1.5 text-[13px] align-middle"
const TABLE = (head, rows) => `<div class="overflow-x-auto rounded-lg border border-gray-200"><table class="w-full"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table></div>`
const fold = (s) => String(s ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase()
const PILL_WARN = "inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[13px] whitespace-nowrap"

export default class extends Controller {
  static targets = ["pane", "body"]
  static values = { url: String, interacUrl: String, personUrl: String, listsUrl: String, week: String, text: Object }

  t(key, vars = {}) {
    let s = key.split(".").reduce((o, k) => o?.[k], this.textValue) ?? ""
    for (const [k, v] of Object.entries(vars)) s = s.replace(`%{${k}}`, v)
    return s
  }

  // A section button on the left: links, codes, people or export. The
  // mapping lives in `state` (Stimulus owns `this.data`).
  async show(e) {
    this.section = e.currentTarget.dataset.section
    this.query = ""
    if (!this.state) {
      this.bodyTarget.innerHTML = `<p class="text-[13px] text-gray-400">${esc(this.t("loading"))}</p>`
      const data = await this._request("GET", `${this.urlValue}?week=${encodeURIComponent(this.weekValue)}`)
      if (!data) return
      this.state = data
    }
    this._render()
  }

  _render() {
    if (!this.hasBodyTarget) return
    if (this.section === "links") this._links()
    else if (this.section === "codes") this._codes()
    else if (this.section === "people") this._people()
    else if (this.section === "export") this._export()
  }

  _head(title, help) {
    return `<div class="mb-3"><h4 class="text-[15px] font-semibold text-gray-900">${esc(title)}</h4><p class="text-[13px] text-gray-500">${esc(help)}</p></div>`
  }

  // ── Word search (K8-22) ────────────────────────────────────────────

  // The box sits under the section's title and stays on top while the list scrolls.
  _searchRow(shown, total) {
    const q = this.query || ""
    return `<div class="sticky top-0 z-10 -mx-5 px-5 py-2 mb-2 bg-white flex items-center gap-2">
      <input type="search" value="${esc(q)}" data-time-mapping-search data-action="input->time-mapping#search keydown.esc->time-mapping#clearSearch"
        placeholder="${esc(this.t("search_ph"))}" aria-label="${esc(this.t("search_ph"))}" class="flex-1 ${INPUT}">
      <span class="text-[13px] text-gray-500 tabular-nums whitespace-nowrap">${q.trim() ? esc(this.t("search_count", { shown, total })) : ""}</span>
    </div>`
  }

  _hit(...parts) {
    const words = fold(this.query).split(/\s+/).filter(Boolean)
    if (!words.length) return true
    const text = fold(parts.filter((v) => v !== null && v !== undefined).join(" "))
    return words.every((w) => text.includes(w))
  }

  _none() {
    return `<p class="py-2 text-[13px] text-gray-400">${esc(this.t("search_none", { q: (this.query || "").trim() }))}</p>`
  }

  // Each letter redraws the list; the box keeps the focus and the caret.
  search(e) {
    this.query = e.currentTarget.value
    const at = e.currentTarget.selectionStart
    this._redraw()
    const box = this.bodyTarget.querySelector("[data-time-mapping-search]")
    if (box) {
      box.focus()
      if (at !== null && at !== undefined) box.setSelectionRange(at, at)
    }
  }

  // Escape empties the search first; only an empty search lets Escape close the pop-up.
  clearSearch(e) {
    if (!this.query) return
    e.stopPropagation()
    this.query = ""
    this._redraw()
    this.bodyTarget.querySelector("[data-time-mapping-search]")?.focus()
  }

  // The export draws from what it already fetched; the rest from the mapping.
  _redraw() {
    if (this.section === "export") this._drawExport()
    else this._render()
  }

  _phaseList() {
    return `<datalist id="tm-phase-numbers">${(this.state.phase_numbers || []).map((p) => `<option value="${esc(p.number)}">${esc(p.description || "")}</option>`).join("")}</datalist>`
  }

  // ── Projects → Jobs ────────────────────────────────────────────────

  _links() {
    const { targets = [], jobs = [] } = this.state
    const missing = targets.filter((t) => !t.job_id && t.hours > 0).length
    const opts = (sel) => `<option value="">${esc(this.t("map.pick_job"))}</option>` +
      jobs.map((j) => `<option value="${j.id}" ${j.id === sel ? "selected" : ""}>${esc(j.label)}</option>`).join("")
    const shown = targets.filter((t) => this._hit(t.name, this.t(t.type === "company" ? "map.type_company" : "map.type_project"),
      jobs.find((j) => j.id === t.job_id)?.label || this.t("map.no_job")))
    const rows = shown.map((t) => `<tr class="border-t border-gray-200">
      <td class="${TD} text-gray-900">${esc(t.name)}</td>
      <td class="${TD} text-gray-500 whitespace-nowrap">${esc(this.t(t.type === "company" ? "map.type_company" : "map.type_project"))}</td>
      <td class="${TD}"><div class="flex items-center gap-2"><select class="${SEL} max-w-xs" data-type="${t.type}" data-id="${t.id}" data-action="change->time-mapping#setLink" aria-label="${esc(t.name)}">${opts(t.job_id)}</select>
        ${t.job_id ? "" : `<span class="${PILL_WARN}">${esc(this.t("map.no_job"))}</span>`}</div></td>
      <td class="${TD} text-right tabular-nums text-gray-700">${t.hours ? t.hours.toFixed(1) : "—"}</td></tr>`).join("")
    this.bodyTarget.innerHTML = this._head(this.t("map.links_title"), this.t("map.links_help")) +
      (missing ? `<p class="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900">${esc(this.t("map.links_missing", { count: missing }))}</p>` : "") +
      (jobs.length ? "" : `<p class="mb-3 text-[13px] text-gray-500">${esc(this.t("map.no_jobs_yet"))}</p>`) +
      this._searchRow(shown.length, targets.length) +
      (shown.length || !targets.length
        ? TABLE(`<th class="${TH}">${esc(this.t("map.col_conslog"))}</th><th class="${TH}">${esc(this.t("map.col_type"))}</th><th class="${TH}">${esc(this.t("map.col_job"))}</th><th class="${TH} text-right">${esc(this.t("map.col_hours"))}</th>`, rows)
        : this._none())
  }

  async setLink(e) {
    const { type, id } = e.currentTarget.dataset
    const data = await this._request("PATCH", `${this.urlValue}/link`,
      { target_type: type, target_id: Number(id), job_id: e.currentTarget.value || null, week: this.weekValue })
    if (!data) return
    this.state = data
    this._render()
    showToast(this.t("saved"))
  }

  // ── Cost codes → Phases ────────────────────────────────────────────

  _codes() {
    const codes = this.state.cost_codes || []
    const shown = codes.filter((c) => this._hit(c.key, c.name, c.phase_number, c.phase_number ? this._phaseName(c.phase_number) : this.t("map.no_phase")))
    const rows = shown.map((c) => `<tr class="border-t border-gray-200">
      <td class="${TD}"><span class="font-medium text-gray-900">${esc(c.key)}</span> <span class="text-gray-600">${esc(c.name || "")}</span></td>
      <td class="${TD}"><input type="text" list="tm-phase-numbers" maxlength="40" value="${esc(c.phase_number || "")}" placeholder="${esc(this.t("map.phase_ph"))}"
        data-id="${c.id}" data-action="change->time-mapping#setCodePhase" aria-label="${esc(c.key)}" class="w-36 ${INPUT}"></td>
      <td class="${TD} text-gray-500">${c.phase_number ? esc(this._phaseName(c.phase_number)) : `<span class="${PILL_WARN}">${esc(this.t("map.no_phase"))}</span>`}</td></tr>`).join("")
    // The phase rule (K6-8 round 2): fill phases from the job's own phase names.
    const rule = `<label class="mb-3 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 cursor-pointer">
      <input type="checkbox" class="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-brand focus:ring-brand" ${this.state.phase_by_name ? "checked" : ""} data-action="change->time-mapping#setRule">
      <span><span class="block text-[13px] font-semibold text-gray-900">${esc(this.t("map.rule_label"))}</span><span class="block text-[13px] text-gray-500">${esc(this.t("map.rule_help"))}</span></span></label>`
    this.bodyTarget.innerHTML = this._head(this.t("map.codes_title"), this.t("map.codes_help")) + rule +
      (codes.length
        ? this._searchRow(shown.length, codes.length) + (shown.length
          ? TABLE(`<th class="${TH}">${esc(this.t("map.col_code"))}</th><th class="${TH}">${esc(this.t("map.col_phase"))}</th><th class="${TH}">${esc(this.t("map.col_phase_name"))}</th>`, rows)
          : this._none())
        : `<p class="text-[13px] text-gray-500">${esc(this.t("map.empty_codes"))}</p>`) +
      this._phaseList()
  }

  // The description of a phase number, from whichever job uses it.
  _phaseName(number) {
    const p = (this.state.phase_numbers || []).find((x) => x.number === number)
    return p ? (p.description || "") : this.t("map.phase_unknown")
  }

  async setCodePhase(e) {
    const id = e.currentTarget.dataset.id
    const phase = e.currentTarget.value.trim()
    const data = await this._request("PATCH", `${this.listsUrlValue}/cost_codes/${id}`, { phase_number: phase })
    if (!data) return
    const code = (this.state.cost_codes || []).find((c) => String(c.id) === String(id))
    if (code) code.phase_number = phase
    this._render()
    showToast(this.t("saved"))
  }

  // ── People → Labor phase ───────────────────────────────────────────

  _people() {
    const { people = [], labor_types: types = [] } = this.state
    const active = types.filter((l) => l.active)
    const opts = (sel) => `<option value="">—</option>` + active.map((l) => `<option value="${l.id}" ${l.id === sel ? "selected" : ""}>${esc(l.name)}</option>`).join("")
    const phaseOf = (id) => active.find((l) => l.id === id)?.key || ""
    const shown = people.filter((p) => this._hit(p.name, p.employee_number, active.find((l) => l.id === p.labor_type_id)?.name, phaseOf(p.labor_type_id)))
    const shownTypes = types.filter((l) => this._hit(l.name, l.key))
    const rows = shown.map((p) => `<tr class="border-t border-gray-200">
      <td class="${TD} text-gray-900">${esc(p.name)}</td>
      <td class="${TD} tabular-nums text-gray-700">${esc(p.employee_number || "—")}</td>
      <td class="${TD}"><select class="${SEL}" data-user-id="${p.user_id}" data-action="change->time-mapping#setPerson" aria-label="${esc(p.name)}">${opts(p.labor_type_id)}</select></td>
      <td class="${TD} tabular-nums text-gray-700">${p.labor_type_id ? (esc(phaseOf(p.labor_type_id)) || `<span class="${PILL_WARN}">${esc(this.t("map.no_phase"))}</span>`) : "—"}</td></tr>`).join("")
    const typeRows = shownTypes.map((l) => `<tr class="border-t border-gray-200 ${l.active ? "" : "opacity-60"}">
      <td class="${TD} text-gray-900">${esc(l.name)}</td>
      <td class="${TD} tabular-nums text-gray-700">${esc(l.key || "—")}</td>
      <td class="${TD} text-right"><button type="button" class="px-2 py-0.5 rounded-lg text-[13px] cursor-pointer ${l.active ? "text-gray-600 hover:bg-gray-100" : "text-brand hover:bg-gray-100"}"
        data-id="${l.id}" data-active="${!l.active}" data-action="click->time-mapping#toggleLaborType">${esc(this.t(l.active ? "archive" : "restore"))}</button></td></tr>`).join("")
    this.bodyTarget.innerHTML = this._head(this.t("map.people_title"), this.t("map.people_help")) +
      this._searchRow(shown.length, people.length) +
      (shown.length || !people.length
        ? TABLE(`<th class="${TH}">${esc(this.t("map.col_person"))}</th><th class="${TH}">${esc(this.t("map.col_employee"))}</th><th class="${TH}">${esc(this.t("map.col_labor_type"))}</th><th class="${TH}">${esc(this.t("map.col_labor_phase"))}</th>`, rows)
        : this._none()) +
      `<h5 class="mt-5 text-[13px] font-semibold text-gray-900">${esc(this.t("map.labor_types_title"))}</h5>
       <p class="mb-2 text-[13px] text-gray-500">${esc(this.t("map.labor_types_help"))}</p>
       <div class="flex flex-wrap items-center gap-2 mb-2">
         <input type="text" maxlength="60" placeholder="${esc(this.t("map.labor_type_ph"))}" aria-label="${esc(this.t("map.col_labor_type"))}" data-time-mapping-new-type class="w-48 ${INPUT}">
         <input type="text" maxlength="40" list="tm-phase-numbers" placeholder="${esc(this.t("map.phase_ph"))}" aria-label="${esc(this.t("map.col_phase"))}" data-time-mapping-new-phase class="w-36 ${INPUT}">
         <button type="button" data-action="click->time-mapping#addLaborType" class="px-3 py-1 rounded-lg text-[13px] font-semibold text-white bg-brand hover:bg-brand-dark cursor-pointer">${esc(this.t("add"))}</button>
       </div>` +
      (shownTypes.length ? TABLE(`<th class="${TH}">${esc(this.t("map.col_labor_type"))}</th><th class="${TH}">${esc(this.t("map.col_phase"))}</th><th class="${TH}"></th>`, typeRows) : "") +
      this._phaseList()
  }

  async setPerson(e) {
    const userId = Number(e.currentTarget.dataset.userId)
    const laborTypeId = e.currentTarget.value ? Number(e.currentTarget.value) : null
    const data = await this._request("PATCH", this.personUrlValue, { user_id: userId, labor_type_id: laborTypeId })
    if (!data) return
    const person = (this.state.people || []).find((p) => p.user_id === userId)
    if (person) person.labor_type_id = data.labor_type_id ?? laborTypeId
    this._render()
    showToast(this.t("saved"))
  }

  async addLaborType() {
    const nameEl = this.bodyTarget.querySelector("[data-time-mapping-new-type]")
    const phaseEl = this.bodyTarget.querySelector("[data-time-mapping-new-phase]")
    const name = nameEl?.value.trim()
    if (!name) {
      showToast(this.t("map.name_required"), "alert")
      nameEl?.focus()
      return
    }
    const data = await this._request("POST", `${this.listsUrlValue}/labor_types`, { key: phaseEl?.value.trim() || "", name })
    if (!data) return
    await this._reload()
    showToast(this.t("added_one"))
  }

  async toggleLaborType(e) {
    const { id, active } = e.currentTarget.dataset
    const data = await this._request("PATCH", `${this.listsUrlValue}/labor_types/${id}`, { active: active === "true" })
    if (!data) return
    await this._reload()
    showToast(this.t(active === "true" ? "restored_toast" : "archived_toast"))
  }

  async setRule(e) {
    const on = e.currentTarget.checked
    const data = await this._request("PATCH", `${this.urlValue}/rule`, { phase_by_name: on })
    if (!data) {
      e.currentTarget.checked = !on
      return
    }
    this.state = data
    this._render()
    showToast(this.t("saved"))
  }

  // ── Export ─────────────────────────────────────────────────────────

  async _export() {
    const head = this._head(this.t("map.export_title"), this.t("map.export_help"))
    this.bodyTarget.innerHTML = head + `<p class="text-[13px] text-gray-400">${esc(this.t("loading"))}</p>`
    const data = await this._request("GET", `${this.interacUrlValue}.json?week=${encodeURIComponent(this.weekValue)}`)
    if (!data || this.section !== "export") return
    this.exportData = data
    this._drawExport()
  }

  _drawExport() {
    const data = this.exportData
    if (!data) return
    const head = this._head(this.t("map.export_title"), this.t("map.export_help"))
    const s = data.summary || {}
    const stat = (label, value, bad = false) => `<div class="rounded-lg border ${bad && value ? "border-red-300" : "border-gray-200"} px-3 py-2">
      <div class="text-[13px] text-gray-500">${esc(label)}</div><div class="text-lg font-semibold tabular-nums ${bad && value ? "text-red-600" : "text-gray-900"}">${value}</div></div>`
    const cols = ["job", "phase", "date", "empl", "equip", "reference", "hours"]
    const missingText = (r) => (r.missing || []).map((m) => this.t(`map.missing_${m}`)).join(", ")
    const all = data.rows || []
    const shown = all.filter((r) => this._hit(...cols.map((k) => r[k]), missingText(r)))
    const rows = shown.map((r) => `<tr class="border-t border-gray-200 ${r.missing?.length ? "bg-red-50" : ""}">` +
      cols.map((k) => `<td class="${TD} tabular-nums whitespace-nowrap">${r[k] === "" || r[k] == null ? "—" : esc(r[k])}</td>`).join("") +
      `<td class="${TD} text-red-700">${esc(missingText(r))}</td></tr>`).join("")
    const day = (iso) => { const d = new Date(`${iso}T00:00:00`); return isNaN(d) ? iso : d.toLocaleDateString(document.documentElement.lang || undefined, { month: "short", day: "numeric" }) }
    const href = `${this.interacUrlValue}.csv?week=${encodeURIComponent(this.weekValue)}`
    this.bodyTarget.innerHTML = head +
      `<div class="flex flex-wrap items-center gap-2 mb-3">
         <span class="text-[13px] text-gray-700">${esc(this.t("map.week", { from: day(data.week_start), to: day(data.week_end) }))}</span>
         <a href="${esc(href)}" class="px-3 py-1 rounded-lg text-[13px] font-semibold text-white bg-brand hover:bg-brand-dark">${esc(this.t("map.download"))}</a>
       </div>
       <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">${stat(this.t("map.lines"), s.lines ?? 0)}${stat(this.t("map.people"), s.people ?? 0)}${stat(this.t("map.jobs"), s.jobs ?? 0)}${stat(this.t("map.missing"), s.missing ?? 0, true)}</div>` +
      (all.length
        ? this._searchRow(shown.length, all.length) + (rows
          ? TABLE(["JOB", "PHASE", "DATE", "EMPL", "EQUIP", "REFERENCE", "HOURS"].map((h) => `<th class="${TH}">${h}</th>`).join("") + `<th class="${TH}">${esc(this.t("map.col_missing"))}</th>`, rows)
          : this._none())
        : `<p class="text-[13px] text-gray-500">${esc(this.t("map.empty_export"))}</p>`) +
      `<p class="mt-2 text-[13px] text-gray-500">${esc(this.t("map.export_note"))}</p>`
  }

  // ── Plumbing ───────────────────────────────────────────────────────

  async _reload() {
    const data = await this._request("GET", `${this.urlValue}?week=${encodeURIComponent(this.weekValue)}`)
    if (!data) return
    this.state = data
    this._render()
  }

  async _request(method, url, body) {
    const headers = { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "", "Accept": "application/json" }
    let payload
    if (body !== undefined) {
      headers["Content-Type"] = "application/json"
      payload = JSON.stringify(body)
    }
    try {
      const res = await fetch(url, { method, headers, body: payload, credentials: "same-origin" })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        showToast(data.error || this.t("errors.generic"), "alert")
        return null
      }
      return data
    } catch (_) {
      showToast(this.t("errors.generic"), "alert")
      return null
    }
  }
}
